import { BeneficiaryApiUrls, BeneficiaryVisibilityType } from "@/modules/beneficiary/common/enum";
import type {
  IAddBeneficiaryEntryCredentials,
  IAttachmentValidationCredentials,
  IAttachmentValidationResponse,
  IBeneficiariesCredentials,
  IBeneficiariesResponse,
  IBeneficiary,
  IBeneficiaryDetail,
  IBeneficiaryDetailsCredentials,
  IChangeBeneficiaryVisibilityCredentials,
  ICreateBeneficiaryRequest,
  IDeleteBeneficiaryCredentials,
  IDeleteBeneficiaryEntryCredentials,
  IRenameBeneficiaryCredentials,
  IUpdateBeneficiaryEntryCredentials,
  TBeneficiaryDetailsResponse,
} from "@/modules/beneficiary/common/types";
import { buildRoute, generateQuery, withIdempotency } from "@/shared/helpers";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "app/axios";

export const beneficiaryApi = createApi({
  reducerPath: "beneficiaryApi",
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    getBeneficiaries: builder.query<IBeneficiariesResponse, IBeneficiariesCredentials>({
      query: ({ type, name, pageSize, pageIndex }) => {
        const query = generateQuery({
          visibility: type,
          name,
          pageSize,
          pageIndex,
        });
        return { url: `${BeneficiaryApiUrls.Beneficiaries}?${query}` };
      },
    }),
    getBeneficiaryDetails: builder.query<TBeneficiaryDetailsResponse, IBeneficiaryDetailsCredentials>(
      {
        query: ({
          name,
          entryType,
          pageSize,
          pageIndex,
          type = BeneficiaryVisibilityType.All,
        }) => {
          const query = generateQuery({
            visibility: type,
            name,
            entryType,
            pageSize,
            pageIndex,
          });
          return { url: `${BeneficiaryApiUrls.BeneficiaryDetails}?${query}` };
        },
      }),
    getBeneficiaryById: builder.query<IBeneficiaryDetail, { beneficiaryId: string }>({
      query: ({ beneficiaryId }) => ({
        url: `${BeneficiaryApiUrls.Beneficiaries}/${beneficiaryId}`,
      }),
    }),
    createBeneficiary: builder.mutation<string, ICreateBeneficiaryRequest>(
      withIdempotency<ICreateBeneficiaryRequest>(BeneficiaryApiUrls.Beneficiaries)
    ),
    deleteBeneficiary: builder.query<void, IDeleteBeneficiaryCredentials>({
      query: ({ id }) => ({
        url: `${BeneficiaryApiUrls.Beneficiaries}/${id}`,
        method: "DELETE",
      }),
    }),
    renameBeneficiary: builder.mutation<IBeneficiary, IRenameBeneficiaryCredentials>({
      query: ({ id, name }) => ({
        url: buildRoute(BeneficiaryApiUrls.BeneficiaryRename, { beneficiaryId: id }),
        method: "PATCH",
        data: { name },
      }),
    }),
    changeVisibility: builder.mutation<IBeneficiary, IChangeBeneficiaryVisibilityCredentials>({
      query: ({ id, isAvailableForOrgUsers }) => ({
        url: buildRoute(BeneficiaryApiUrls.BeneficiaryVisibility, { beneficiaryId: id }),
        method: "PUT",
        data: { isAvailableForOrgUsers },
      }),
    }),
    addBeneficiaryAccount: builder.mutation<IBeneficiary, IAddBeneficiaryEntryCredentials>({
      query: ({ beneficiaryId, entry, idempotencyKey }) => ({
        url: buildRoute(BeneficiaryApiUrls.BeneficiaryAccount, { beneficiaryId }),
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        data: entry,
      }),
    }),
    updateBeneficiaryAccount: builder.mutation<IBeneficiary, IUpdateBeneficiaryEntryCredentials>({
      query: ({ beneficiaryId, beneficiaryEntryId, entry }) => ({
        url: buildRoute(
          BeneficiaryApiUrls.BeneficiaryAccountItem,
          { beneficiaryId, beneficiaryEntryId }
        ),
        method: "PUT",
        data: entry,
      }),
    }),
    deleteBeneficiaryAccount: builder.mutation<void, IDeleteBeneficiaryEntryCredentials>({
      query: ({ beneficiaryId, beneficiaryEntryId }) => ({
        url: buildRoute(
          BeneficiaryApiUrls.BeneficiaryAccountItem,
          { beneficiaryId, beneficiaryEntryId }
        ),
        method: "DELETE",
      }),
    }),
    addBeneficiaryCard: builder.mutation<IBeneficiary, IAddBeneficiaryEntryCredentials>({
      query: ({ beneficiaryId, entry, idempotencyKey }) => ({
        url: buildRoute(BeneficiaryApiUrls.BeneficiaryCard, { beneficiaryId }),
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        data: entry,
      }),
    }),
    updateBeneficiaryCard: builder.mutation<IBeneficiary, IUpdateBeneficiaryEntryCredentials>({
      query: ({ beneficiaryId, beneficiaryEntryId, entry }) => ({
        url: buildRoute(
          BeneficiaryApiUrls.BeneficiaryCardItem,
          { beneficiaryId, beneficiaryEntryId }
        ),
        method: "PUT",
        data: entry,
      }),
    }),
    deleteBeneficiaryCard: builder.mutation<void, IDeleteBeneficiaryEntryCredentials>({
      query: ({ beneficiaryId, beneficiaryEntryId }) => ({
        url: buildRoute(
          BeneficiaryApiUrls.BeneficiaryCardItem,
          { beneficiaryId, beneficiaryEntryId }
        ),
        method: "DELETE",
      }),
    }),
    attachmentValidation: builder.mutation<IAttachmentValidationResponse, IAttachmentValidationCredentials>(
      {
        query: (data) => ({
          url: `${BeneficiaryApiUrls.AttachmentValidation}`,
          method: "POST",
          data,
        }),
      }),
  }),
});

export const {
  useGetBeneficiariesQuery,
  useGetBeneficiaryByIdQuery,
  useCreateBeneficiaryMutation,
  useLazyDeleteBeneficiaryQuery,
  useRenameBeneficiaryMutation,
  useAddBeneficiaryAccountMutation,
  useUpdateBeneficiaryAccountMutation,
  useDeleteBeneficiaryAccountMutation,
  useAddBeneficiaryCardMutation,
  useUpdateBeneficiaryCardMutation,
  useDeleteBeneficiaryCardMutation,
  useAttachmentValidationMutation,
  useChangeVisibilityMutation,
  useGetBeneficiaryDetailsQuery,
} = beneficiaryApi;
